import { useCallback, useRef } from 'react';
import type { ReactElement } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { env } from '@/env';

interface InvisibleTurnstileApi {
  Widget: () => ReactElement;
  getToken: () => Promise<string>;
  reset: () => void;
}

/**
 * Hook for managing an invisible Cloudflare Turnstile captcha.
 *
 * This hook provides a reusable pattern for executing captcha verification
 * on-demand during form submission.
 *
 * @returns {InvisibleTurnstileApi} Object containing:
 *   - `Widget`: React component to render once in your form
 *   - `getToken`: Async function to retrieve a captcha token on form submit
 *   - `reset`: Function to reset the captcha widget
 *
 */
export function useInvisibleTurnstile(): InvisibleTurnstileApi {
  const instanceRef = useRef<TurnstileInstance | null>(null);

  const Widget = useCallback(
    () => (
      <Turnstile
        ref={instanceRef}
        siteKey={env.VITE_TURNSTILE_SITE_KEY}
        options={{ size: 'invisible' }}
      />
    ),
    []
  );

  const getToken = useCallback(async () => {
    if (!instanceRef.current) {
      throw new Error('Captcha not initialized');
    }
    const token = await (
      instanceRef.current as unknown as {
        getResponsePromise: () => Promise<string | undefined>;
      }
    ).getResponsePromise();
    if (!token) {
      throw new Error('Failed to retrieve captcha token');
    }
    return token;
  }, []);

  const reset = useCallback(() => {
    instanceRef.current?.reset();
  }, []);

  return { Widget, getToken, reset };
}
